let accessToken: string | null = null;
let sessionGeneration = 0;
const objectUrls = new Set<string>();
function subject(token: string | null) {
  try { return JSON.parse(atob((token?.split(".")[1] ?? "").replace(/-/g, "+").replace(/_/g, "/"))).sub as string; } catch { return token; }
}
export function setFileAccessToken(token: string | null) {
  const changed = token === null || subject(token) !== subject(accessToken);
  accessToken = token;
  if (changed) { sessionGeneration++; for (const url of objectUrls) URL.revokeObjectURL(url); objectUrls.clear(); }
}
export function privateObjectUrl(blob: Blob) { const url = URL.createObjectURL(blob); objectUrls.add(url); return url; }
export function revokePrivateObjectUrl(url: string) { objectUrls.delete(url); URL.revokeObjectURL(url); }

function isProtectedFile(url: URL) {
  const configured = import.meta.env.VITE_CONVEX_URL as string | undefined;
  if (!configured) return false;
  const origin = new URL(configured).origin.replace(/\.convex\.cloud$/, ".convex.site");
  return url.origin === origin && /^\/files\/(attachment|profile|speech)\/[a-zA-Z0-9_-]+$/.test(url.pathname);
}

export async function fetchPrivateFile(fileUrl: string, signal?: AbortSignal): Promise<Blob> {
  const url = new URL(fileUrl, window.location.href);
  if (!isProtectedFile(url)) {
    // Compatibility during release and local upload previews. Session tokens
    // are never sent to arbitrary URLs, redirects or storage bearer URLs.
    const response = await fetch(fileUrl, { signal: signal ?? null, cache: "no-store", credentials: "omit", redirect: "error" });
    if (!response.ok) throw new Error("The file could not be opened. Please refresh Yodl.");
    return await response.blob();
  }
  const chunks: Blob[] = [];
  const generation = sessionGeneration;
  let offset = 0;
  let total: number | undefined;
  let type = "application/octet-stream";
  do {
    const token = accessToken;
    if (!token) throw new Error("Please sign in to open this file.");
    const response = await fetch(url.href, { headers: { Authorization: `Bearer ${token}`, Range: `bytes=${offset}-${offset + 4 * 1024 * 1024 - 1}` }, cache: "no-store", credentials: "omit", redirect: "error", signal: signal ?? null, referrerPolicy: "no-referrer" });
    if (!response.ok) throw new Error(response.status === 401 ? "Please sign in again to open this file." : response.status === 403 ? "You do not have access to this file." : "The file could not be opened. Please try again.");
    if (accessToken === null || generation !== sessionGeneration) throw new Error("Please sign in to open this file.");
    const match = /^bytes (\d+)-(\d+)\/(\d+)$/.exec(response.headers.get("Content-Range") ?? "");
    if (response.status !== 206 || !match || Number(match[1]) !== offset || (total !== undefined && Number(match[3]) !== total)) throw new Error("The file could not be downloaded completely.");
    total = Number(match[3]);
    const chunk = await response.blob();
    if (generation !== sessionGeneration) throw new Error("Please sign in to open this file.");
    if (!Number.isSafeInteger(total) || total < 1 || chunk.size !== Number(match[2]) - offset + 1 || chunk.size < 1 || chunk.size > 4 * 1024 * 1024) throw new Error("The file could not be downloaded completely.");
    type = response.headers.get("Content-Type") || type;
    chunks.push(chunk); offset += chunk.size;
  } while (offset < total);
  if (offset !== total) throw new Error("The file could not be downloaded completely.");
  return new Blob(chunks, { type });
}
